import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';

import { AppointmentId } from '@dreryk/egabinet-appointment-published-language-api';
import { VisitSettlement, VisitSettlementQuery } from '@dreryk/egabinet-financial-settlement-frontend-api';
import { PurchaseId } from '@dreryk/egabinet-financial-settlement-published-language-api';
import {
   CurrentHealthcareProviderInvoiceIntegrationEnabledQuery,
   CurrentHealthcareProviderInvoicePdfQuery,
} from '@dreryk/egabinet-invoice-frontend-api';

import { performCqrsQueryExists, performCqrsQueryFind, useCqrsGet } from '@dreryk/command-query-react-common';
import { convertDateToLocalDate, Translate, useBoolean } from '@dreryk/react-common';
import { dispatchSnackbarNegativeEvent, Dropdown } from '@dreryk/react-components';
import { Slot } from '@dreryk/watermelon';

import { protectFeature } from '~libs.shared.system';
import { downloadInvoiceAsPdf, InvoiceCreateModal, InvoiceCreateModalLoader } from '~legacy.invoice';

import { prescriptionVisitsTranslationKeys } from '../../../../translations';
import { visitSettlementTranslationKeys } from '../../../../visitSettlement/translations';
import { usePrescriptionVisitSettlementGlobalState } from '../../settlement/usePrescriptionVisitSettlementGlobalState';

type UsePrescriptionVisitInvoiceCreateDropdownItemOptions = {
   patientId: number;
   visitDate: Date;
   appointmentId: number;
};

type UsePrescriptionVisitInvoiceCreateDropdownItemOutput = {
   InvoiceCreateDropdownItem: () => ReactElement | null;
   invoiceCreateModal: ReactElement | null;
};

// eslint-disable-next-line max-lines-per-function
export const usePrescriptionVisitInvoiceCreateDropdownItem = (
   options: UsePrescriptionVisitInvoiceCreateDropdownItemOptions
): UsePrescriptionVisitInvoiceCreateDropdownItemOutput => {
   const { patientId, visitDate, appointmentId } = options;
   const [visitSettlementId, setVisitSettlementId] = useState<number | null>(null);
   const [{ setTrue: openModal, setFalse: closeModal }, modalOpened] = useBoolean();
   const [visitUnsettled, setVisitUnsettled] = useState<boolean | null>(true);
   const { getSettlement } = usePrescriptionVisitSettlementGlobalState();
   const invoiceCreateAvailable = protectFeature('creatingInvoices');
   const privateMedicalServicesReadOnlyAvailable = protectFeature('privateMedicalServicesReadOnly');

   const { data: invoiceProviderIntegrationEnabled } = useCqrsGet<boolean>(
      () => new CurrentHealthcareProviderInvoiceIntegrationEnabledQuery(),
      {
         enabled: invoiceCreateAvailable,
      }
   );

   useEffect(() => {
      const status = getSettlement(appointmentId)?.settlementStatus ?? null;
      setVisitUnsettled(!status || status === 'UNSETTLED');
   }, [appointmentId, getSettlement]);

   const handleInvoiceCreateOptionClick = useCallback(
      async (hide: () => void) => {
         try {
            const currentVisitSettlement = await performCqrsQueryFind<VisitSettlement>(() => {
               const query = new VisitSettlementQuery();
               query.purchaseIds = [PurchaseId.forAppointment(AppointmentId.of(appointmentId))];
               return query;
            });

            if (currentVisitSettlement) {
               const invoiceExists = await performCqrsQueryExists(() => {
                  const query = new CurrentHealthcareProviderInvoicePdfQuery();
                  query.referenceId = Slot.of(currentVisitSettlement.id.toString());
                  return query;
               });

               if (invoiceExists) {
                  await downloadInvoiceAsPdf(currentVisitSettlement.id.toString())();
                  hide();
               } else {
                  setVisitSettlementId(currentVisitSettlement.id ?? null);
                  openModal();
                  hide();
               }
            }
         } catch {
            dispatchSnackbarNegativeEvent(
               visitSettlementTranslationKeys.extensions.appointments.dropdownOption.errors.unexpectedError
            );
         }
      },
      [appointmentId, openModal]
   );

   return useMemo(
      () => ({
         InvoiceCreateDropdownItem: () => {
            if (privateMedicalServicesReadOnlyAvailable || visitUnsettled || !invoiceProviderIntegrationEnabled) {
               return null;
            }
            return (
               <Dropdown.Item
                  testId={'invoiceCreateOption'}
                  onClick={async (hide) => handleInvoiceCreateOptionClick(hide)}
               >
                  <Translate>{prescriptionVisitsTranslationKeys.invoice}</Translate>
               </Dropdown.Item>
            );
         },
         invoiceCreateModal:
            modalOpened && visitSettlementId ? (
               <InvoiceCreateModalLoader referenceId={visitSettlementId.toString()}>
                  {({ fiscalDocuments, fiscalPrintEnabled }) => (
                     <InvoiceCreateModal
                        patientId={patientId}
                        visitDate={convertDateToLocalDate(visitDate)}
                        appointmentId={appointmentId}
                        visitSettlementId={visitSettlementId}
                        onClose={closeModal}
                        fiscalPrintEnabled={fiscalPrintEnabled}
                        fiscalDocuments={fiscalDocuments}
                     />
                  )}
               </InvoiceCreateModalLoader>
            ) : null,
      }),
      [
         appointmentId,
         visitUnsettled,
         privateMedicalServicesReadOnlyAvailable,
         invoiceProviderIntegrationEnabled,
         handleInvoiceCreateOptionClick,
         modalOpened,
         visitSettlementId,
         closeModal,
         patientId,
         visitDate,
      ]
   );
};
